import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';
import { useSystemOptions } from '@/contexts/SystemOptionsContext';
import {
  X,
  AlertCircle,
  Copy,
  MoreVertical,
  Trash2,
  TrendingDown,
  Printer,
  ExternalLink,
} from 'lucide-react';
import { AssetDetailHeader } from '@/components/assets/detail/AssetDetailHeader';
import { AssetDetailOverview } from '@/components/assets/detail/AssetDetailOverview';
import { AssetDetailValues } from '@/components/assets/detail/AssetDetailValues';
import { AssetDetailSupplier } from '@/components/assets/detail/AssetDetailSupplier';
import { AssetDetailEquipment } from '@/components/assets/detail/AssetDetailEquipment';
import { AssetDetailCompliance } from '@/components/assets/detail/AssetDetailCompliance';
import { AssetDetailHistory } from '@/components/assets/detail/AssetDetailHistory';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import { generateAlertsForAsset, calculateAlertSummary } from '@/services/alerts-service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AssetDetailSheetProps {
  readonly assetId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly defaultTab?: string;
  readonly onAssetUpdated?: () => void;
}

export function AssetDetailSheet({ 
  assetId, 
  open, 
  onOpenChange, 
  defaultTab = 'overview',
  onAssetUpdated 
}: Readonly<AssetDetailSheetProps>) {
  useToast();
  const { options: systemOptions } = useSystemOptions();
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [editedAsset, setEditedAsset] = useState<Asset | null>(null);
  const [originalPhotos, setOriginalPhotos] = useState<Asset['photos']>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDisposalWizard, setShowDisposalWizard] = useState(false);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);

  useEffect(() => {
    if (open && assetId) {
      loadAsset();
    }
  }, [assetId, open]);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const loadAsset = async () => {
    try {
      setLoading(true);
      const data = await dataService.getAssetById(assetId);
      setAsset(data);
      setEditedAsset(data);
      setOriginalPhotos(data.photos);
    } catch (error) {
      console.error('Failed to load asset:', error);
      toast.error('Failed to load asset details');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedAsset) return;

    try {
      setIsSaving(true);
      await dataService.updateAsset(editedAsset);
      setAsset(editedAsset);
      setOriginalPhotos(editedAsset.photos);
      setIsEditMode(false);
      toast.success('Asset updated successfully');
      onAssetUpdated?.();
    } catch (error) {
      console.error('Failed to save asset:', error);
      toast.error('Failed to save asset');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (asset) {
      setEditedAsset({ ...asset, photos: originalPhotos });
    }
    setIsEditMode(false);
  };

  const handleDuplicate = () => {
    if (!asset) return;
    
    window.open(`/assets/new?mode=create`, '_blank');
  };

  const handleDelete = async () => {
    if (!asset) return;

    try {
      await dataService.deleteAsset(asset.id);
      toast.success('Asset deleted successfully');
      onOpenChange(false);
      onAssetUpdated?.();
    } catch (error) {
      console.error('Failed to delete asset:', error);
      toast.error('Failed to delete asset');
    }
  };

  const handleDisposal = () => {
    setShowDisposalWizard(true);
  };

  const handleDisposalComplete = () => {
    setShowDisposalWizard(false);
    loadAsset();
    onAssetUpdated?.();
  };

  const handlePrintLabel = () => {
    setShowLabelPrintingModal(true);
  };

  const handleOpenInNewTab = () => {
    window.open(`/assets/${assetId}`, '_blank');
  };

  const alerts = useMemo(() => {
    if (!asset) return [];
    return generateAlertsForAsset(asset);
  }, [asset]);

  const alertSummary = useMemo(() => {
    return calculateAlertSummary(alerts);
  }, [alerts]);

  const hasChanges = useMemo(() => {
    if (!asset || !editedAsset) return false;
    return JSON.stringify(asset) !== JSON.stringify(editedAsset);
  }, [asset, editedAsset]);

  if (!open) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[800px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SheetTitle className="text-xl">
                  {loading ? 'Loading...' : asset?.name ?? 'Asset Details'}
                </SheetTitle>
                {asset && (
                  <Badge variant={asset.status === 'inService' ? 'default' : 'secondary'}>
                    {ASSET_STATUS_LABELS[asset.status]}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {alertSummary.totalCount > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {alertSummary.totalCount}
                  </Badge>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleOpenInNewTab}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Open in New Tab
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDuplicate}>
                      <Copy className="mr-2 h-4 w-4" />
                      Duplicate Asset
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePrintLabel}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print Label
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleDisposal}>
                      <TrendingDown className="mr-2 h-4 w-4" />
                      Dispose Asset
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete Asset
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          {loading ? (
            <div className="flex-1 p-6 space-y-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : asset && editedAsset ? (
            <ScrollArea className="flex-1">
              <div className="p-6">
                <AssetDetailHeader
                  asset={editedAsset}
                  onAssetUpdate={setEditedAsset}
                  isEditMode={isEditMode}
                />

                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
                  <TabsList className="grid w-full grid-cols-6">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="values">Values</TabsTrigger>
                    <TabsTrigger value="supplier">Supplier</TabsTrigger>
                    <TabsTrigger value="equipment">Equipment</TabsTrigger>
                    <TabsTrigger value="compliance">Compliance</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-6">
                    <AssetDetailOverview
                      asset={editedAsset}
                      onAssetUpdate={setEditedAsset}
                      isEditMode={isEditMode}
                      systemOptions={systemOptions}
                    />
                  </TabsContent>

                  <TabsContent value="values" className="mt-6">
                    <AssetDetailValues
                      asset={editedAsset}
                      onAssetUpdate={setEditedAsset}
                      isEditMode={isEditMode}
                    />
                  </TabsContent>

                  <TabsContent value="supplier" className="mt-6">
                    <AssetDetailSupplier
                      asset={editedAsset}
                      onAssetUpdate={setEditedAsset}
                      isEditMode={isEditMode}
                    />
                  </TabsContent>

                  <TabsContent value="equipment" className="mt-6">
                    <AssetDetailEquipment
                      asset={editedAsset}
                      onAssetUpdate={setEditedAsset}
                      isEditMode={isEditMode}
                    />
                  </TabsContent>

                  <TabsContent value="compliance" className="mt-6">
                    <AssetDetailCompliance
                      asset={editedAsset}
                      onAssetUpdate={setEditedAsset}
                      isEditMode={isEditMode}
                    />
                  </TabsContent>

                  <TabsContent value="history" className="mt-6">
                    <AssetDetailHistory asset={asset} />
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this asset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disposal Wizard */}
      {asset && (
        <DisposalWizard
          asset={asset}
          open={showDisposalWizard}
          onClose={() => setShowDisposalWizard(false)}
          onComplete={handleDisposalComplete}
        />
      )}

      {/* Label Printing Modal */}
      {asset && (
        <LabelPrintingModal
          open={showLabelPrintingModal}
          onClose={() => setShowLabelPrintingModal(false)}
          assets={[asset]}
        />
      )}
    </>
  );
}
